// --- MAIN VIEW RENDERERS MODULE ---
// Main view rendering functions

import { Topic, Problem } from '../types';
import { state } from '../state';
import { data } from '../data';
import { getToday, shouldShowProblem, updateBreadcrumbs, generateBreadcrumbs } from '../utils';
// renderers import removed to break cycle
import { api } from '../api';
import { ICONS } from './icons';
import { htmlGenerators } from './html-generators';
import { problemCardRenderers } from './problem-cards';
import { AlgorithmCategory, AlgorithmDef } from '../data/algorithms-data';
import { virtualProblemList } from './virtual-problem-list';

export const mainViewRenderers = {
    // Helper to create an action button
    // NOTE: Tailwind JIT requires full class names at build time — dynamic interpolation
    // like hover:\${color} doesn't work. Use a static map for all known color variants.
    _createActionBtn: (icon: string, title: string, colorClass: string, onClick: () => void) => {
        // Static map of background classes to their hover state classes.
        // This ensures Tailwind's JIT compiler can detect and include all classes.
        const hoverClassMap: Record<string, string> = {
            'bg-blue-500/10': 'hover:bg-blue-500/20 hover:text-blue-400',
            'bg-red-500/10': 'hover:bg-red-500/20 hover:text-red-400',
            'bg-green-500/10': 'hover:bg-green-500/20 hover:text-green-400',
            'bg-amber-500/10': 'hover:bg-amber-500/20 hover:text-amber-400',
            'bg-purple-500/10': 'hover:bg-purple-500/20 hover:text-purple-400',
            'bg-slate-500/10': 'hover:bg-slate-500/20 hover:text-slate-400',
        };

        const hoverClasses =
            hoverClassMap[colorClass] || 'hover:bg-slate-500/20 hover:text-slate-400';
        const btn = document.createElement('button');
        btn.className = `category-action-btn p-1 rounded ${colorClass} text-theme-muted ${hoverClasses} transition-colors`;
        btn.title = title;
        btn.innerHTML = icon;
        btn.onclick = onClick;
        return btn;
    },

    // Helper to remove existing action button container
    _removeActionContainer: () => {
        const title = state.elements['currentViewTitle'] as HTMLElement | null;
        const container = title?.nextElementSibling;
        if (container?.classList.contains('category-action-container')) {
            container.remove();
        }
    },

    // Helper to set view title and action buttons
    _setViewTitle: (topicId: string) => {
        const title =
            topicId === 'all' || !topicId
                ? 'All Content'
                : data.topicsData.find((t: Topic) => t.id === topicId)?.title || 'Unknown Topic';

        const viewTitle = state.elements['currentViewTitle'] as HTMLElement | null;
        if (viewTitle) viewTitle.innerText = title;

        mainViewRenderers._removeActionContainer();

        const container = document.createElement('div');
        container.className = 'category-action-container ml-2 flex gap-1';

        if (topicId === 'all' || !topicId) {
            container.appendChild(
                mainViewRenderers._createActionBtn(
                    ICONS.reset,
                    'Reset All Content',
                    'bg-blue-500/10',
                    () => api.resetAll()
                )
            );
        } else {
            container.appendChild(
                mainViewRenderers._createActionBtn(
                    ICONS.reset,
                    'Reset Category',
                    'bg-blue-500/10',
                    () => api.resetCategory(topicId)
                )
            );
            container.appendChild(
                mainViewRenderers._createActionBtn(
                    ICONS.delete,
                    'Delete Category',
                    'bg-red-500/10',
                    () => api.deleteCategory(topicId)
                )
            );
        }

        viewTitle?.insertAdjacentElement('afterend', container);
    },

    // Render main problem view
    renderMainView: async (topicId: string) => {
        state.ui.activeTopicId = topicId || state.ui.activeTopicId;
        const container = state.elements['problemsContainer'] as HTMLElement | null;
        if (container) container.innerHTML = '';

        mainViewRenderers._setViewTitle(state.ui.activeTopicId);
        updateBreadcrumbs(generateBreadcrumbs());

        // Update date filter visibility and populate dropdown based on current filter mode
        const { ui } = await import('../ui/ui');
        ui.updateDateFilterForCurrentMode();

        const today = getToday();
        const visibleCount = { count: 0 };

        const topics =
            state.ui.activeTopicId === 'all' || state.ui.activeTopicId === ''
                ? data.topicsData
                : data.topicsData.filter((t: Topic) => t.id === state.ui.activeTopicId);

        // Collect all visible problems for virtual scrolling
        const allVisibleProblems: Problem[] = [];
        topics.forEach((topic: Topic) => {
            topic.patterns.forEach((pattern) => {
                pattern.problems.forEach((probDef) => {
                    const id = typeof probDef === 'string' ? probDef : probDef.id;
                    const p = state.problems.get(id);
                    if (p) {
                        const searchQuery =
                            (state.elements['problemSearch'] as HTMLInputElement | null)?.value
                                .toLowerCase()
                                .trim() || '';
                        if (shouldShowProblem(p, state.ui.currentFilter, searchQuery, today)) {
                            allVisibleProblems.push(p);
                            visibleCount.count++;
                        }
                    }
                });
            });
        });

        // Try to use virtual scrolling for large lists
        if (container && allVisibleProblems.length > 50) {
            // Clean up any previous virtual scroller
            virtualProblemList.cleanup();

            // Use virtual rendering
            const useVirtual = virtualProblemList.init(container, allVisibleProblems);
            if (useVirtual) {
                // Virtual scrolling initialized successfully
                // Show empty state only in review filter with no visible problems
                const showEmpty = visibleCount.count === 0 && state.ui.currentFilter === 'review';
                (state.elements['emptyState'] as HTMLElement | null)?.classList.toggle(
                    'hidden',
                    !showEmpty
                );
                import('../renderers').then(({ renderers }) => renderers.updateStats());
                return;
            }
        }

        // Fallback to standard rendering for smaller lists
        topics.forEach((topic: Topic) => {
            const section = htmlGenerators.renderTopicSection(
                topic,
                state.ui.activeTopicId,
                today,
                visibleCount
            );
            if (section && container) container.appendChild(section);
        });

        // Show empty state if no visible problems match the current filters
        const hasVisibleProblems = visibleCount.count > 0;

        // Add inline empty state if container is empty (no sections rendered)
        if (!hasVisibleProblems && container) {
            const inlineEmptyState = document.createElement('div');
            inlineEmptyState.className = 'text-center py-12 text-secondary';
            inlineEmptyState.innerHTML = `
                <div class="text-6xl mb-4">🔍</div>
                <p class="text-lg">No problems match your current filters.</p>
                <p class="text-sm mt-2">Try adjusting your search or filter criteria.</p>
            `;
            container.appendChild(inlineEmptyState);
        }

        import('../renderers').then(({ renderers }) => renderers.updateStats());
    },

    // Set algorithm view title
    _setAlgorithmViewTitle: (categoryId: string) => {
        const title =
            categoryId === 'all'
                ? 'All Algorithms'
                : data.algorithmsData.find((c: AlgorithmCategory) => c.id === categoryId)?.title ||
                  'Algorithms';

        const viewTitle = state.elements['currentViewTitle'] as HTMLElement | null;
        if (viewTitle) viewTitle.innerText = title;

        mainViewRenderers._removeActionContainer();

        const container = document.createElement('div');
        container.className = 'category-action-container ml-2 flex gap-1';

        const resetLabel = categoryId === 'all' ? 'Reset All Algorithms' : 'Reset Category';
        container.appendChild(
            mainViewRenderers._createActionBtn(ICONS.reset, resetLabel, 'bg-blue-500/10', () =>
                api.resetAlgorithmCategory(categoryId)
            )
        );

        if (categoryId !== 'all') {
            container.appendChild(
                mainViewRenderers._createActionBtn(
                    ICONS.delete,
                    'Delete Category',
                    'bg-red-500/10',
                    () => api.deleteAlgorithmCategory(categoryId)
                )
            );
        }

        viewTitle?.insertAdjacentElement('afterend', container);
    },

    // Convert algorithm definition to Problem object
    _algorithmToProblem: (algoDef: AlgorithmDef, categoryId: string): Problem => {
        return (
            state.problems.get(algoDef.id) || {
                id: algoDef.id,
                name: algoDef.name,
                url: algoDef.url,
                status: 'unsolved',
                topic: categoryId,
                pattern: 'Algorithms',
                reviewInterval: 0,
                nextReviewDate: null,
                note: '',
                loading: false,
                noteVisible: false,
            }
        );
    },

    // Render algorithms view for a category (treating algorithms as problems)
    renderAlgorithmsView: async (categoryId: string) => {
        state.ui.activeAlgorithmCategoryId = categoryId;
        const container = state.elements['problemsContainer'] as HTMLElement | null;
        if (!container) return;

        container.innerHTML = '';
        mainViewRenderers._setAlgorithmViewTitle(categoryId);
        updateBreadcrumbs(generateBreadcrumbs());

        // Update date filter visibility and populate dropdown based on current filter mode
        const { ui } = await import('../ui/ui');
        ui.updateDateFilterForCurrentMode();

        const today = getToday();
        const visibleCount = { count: 0 };

        const searchQuery =
            (state.elements['problemSearch'] as HTMLInputElement | null)?.value
                .toLowerCase()
                .trim() || '';
        const section = document.createElement('div');
        section.className = 'space-y-6';

        const renderAlgorithmCard = (algoDef: AlgorithmDef, catId: string, grid: HTMLElement) => {
            if (state.deletedProblemIds.has(algoDef.id)) return;

            const problem = mainViewRenderers._algorithmToProblem(algoDef, catId);
            if (!state.problems.has(algoDef.id)) {
                const success = state.setProblem(algoDef.id, problem);
                if (!success) {
                    console.error(
                        '[MainView] Failed to initialize algorithm problem:',
                        algoDef.id,
                        problem
                    );
                    // Skip rendering this card if we can't store the problem
                    return;
                }
            }

            // Apply the same filtering logic as pattern problems
            if (!shouldShowProblem(problem, state.ui.currentFilter, searchQuery, today)) return;

            visibleCount.count++;
            grid.appendChild(problemCardRenderers.createProblemCard(problem));
        };

        if (categoryId === 'all') {
            data.algorithmsData.forEach((category: AlgorithmCategory) => {
                // First, determine which algorithms match all filters
                const matchingAlgorithms: AlgorithmDef[] = [];
                category.algorithms.forEach((algoDef) => {
                    if (state.deletedProblemIds.has(algoDef.id)) return;
                    const problem = mainViewRenderers._algorithmToProblem(algoDef, category.id);
                    if (!state.problems.has(algoDef.id)) {
                        const success = state.setProblem(algoDef.id, problem);
                        if (!success) {
                            console.error(
                                '[MainView] Failed to initialize algorithm problem:',
                                algoDef.id,
                                problem
                            );
                            // Skip this algorithm if we can't store it
                            return;
                        }
                    }
                    if (shouldShowProblem(problem, state.ui.currentFilter, searchQuery, today)) {
                        matchingAlgorithms.push(algoDef);
                    }
                });

                // Only show category header if there are matching algorithms
                if (matchingAlgorithms.length === 0) return;

                const header = document.createElement('h3');
                header.className = 'text-xl font-bold text-theme-bold border-b border-theme pb-2';
                header.textContent = category.title;
                section.appendChild(header);

                const grid = document.createElement('div');
                grid.className = 'grid grid-cols-1 gap-3';
                matchingAlgorithms.forEach((algo) => renderAlgorithmCard(algo, category.id, grid));
                section.appendChild(grid);
            });
        } else {
            const category = data.algorithmsData.find(
                (c: AlgorithmCategory) => c.id === categoryId
            );
            if (!category) return;

            const grid = document.createElement('div');
            grid.className = 'grid grid-cols-1 gap-3';
            category.algorithms.forEach((algo) => renderAlgorithmCard(algo, categoryId, grid));

            // Only append grid if there are matching algorithms
            if (grid.children.length > 0) {
                section.appendChild(grid);
            }
        }

        // Show empty state if no visible problems match the current filters
        const hasVisibleProblems = visibleCount.count > 0;

        // Add inline empty state if section has no content
        if (!hasVisibleProblems) {
            const inlineEmptyState = document.createElement('div');
            inlineEmptyState.className = 'text-center py-12 text-secondary';
            inlineEmptyState.innerHTML = `
                <div class="text-6xl mb-4">🔍</div>
                <p class="text-lg">No problems match your current filters.</p>
                <p class="text-sm mt-2">Try adjusting your search or filter criteria.</p>
            `;
            section.appendChild(inlineEmptyState);
        }

        container.appendChild(section);
        import('../renderers').then(({ renderers }) => renderers.updateStats());
    },
};
